'use client';

import { Clock3 } from 'lucide-react';
import { HubNewsItem, HubType } from '@/types/hubs';
import { formatDateStringWithDelimiter } from '@/utils/formatters/dateFormatter';
import IconAdaptive from '@/components/IconAdaptive';
import TruncateText from '@/components/TruncateText';

export interface HubNewsListUnitProps {
    hubNews: HubNewsItem;
    hubNewsCount?: number;
    onSelectHub?: (hubIndex: string) => void;
    bordered?: boolean;
}

export default function HubNewsListUnit({ hubNews, hubNewsCount, onSelectHub, bordered = false }: HubNewsListUnitProps) {
    const bgClass = (() => {
        switch (hubNews.hub_type) {
            case HubType.spot:
                return 'bg-color-hub-spot-pin';
            case HubType.festa:
                return 'bg-color-hub-festa-pin';
            case HubType.stand:
                return 'bg-color-hub-stand-pin';
            case HubType.studio:
                return 'bg-color-hub-studio-pin';
            case HubType.schoomyers:
                return 'bg-color-hub-schoomyers-pin';
            case HubType.crew:
                return 'bg-color-hub-crew-pin';
            case HubType.stage:
                return 'bg-color-hub-stage-pin';
            case HubType.in:
                return 'bg-color-hub-in-pin';
            default:
                return 'bg-color-co-gray';
        }
    })();

    return (
        <div
            className={`w-full flex flex-col rounded-none sm:rounded-xl bg-color-white-base h-full min-h-[360px] cursor-pointer overflow-hidden ${
                bordered ? 'border border-color-co-gray' : ''
            }`}
            onClick={() => {
                if (hubNews.url) {
                    window.open(hubNews.url, '_blank', 'noopener,noreferrer');
                }
            }}
        >
            <div className="relative w-full overflow-hidden rounded-none sm:rounded-t-xl">
                <div className="aspect-[3/2] w-full bg-color-white-base">
                    <img
                        src={hubNews.eyecatch_resource_url || `/images/hub/map/icon-${hubNews.hub_type}.svg`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="absolute top-2 right-4 font-cantarell font-bold text-xs text-color-white-base rounded-3xl bg-color-base px-4 z-10 uppercase">
                    {hubNews.hub_type}
                </div>
            </div>
            <div className={`h-[4px] w-full ${bgClass}`} />
            <div className="flex flex-col p-3 w-full flex-grow">
                {onSelectHub ? (
                    <button
                        type="button"
                        className="flex flex-wrap justify-start items-baseline w-full gap-x-1 text-left text-color-co-gray-dark hover:text-color-service-selected transition-colors duration-150"
                        onClick={(event) => {
                            event.stopPropagation();
                            onSelectHub(hubNews.hub_index);
                        }}
                    >
                        <span className="font-notojp font-medium text-xs underline decoration-dotted underline-offset-2">
                            {hubNews.hub_name}
                        </span>
                        {hubNews.hub_sub_name && (
                            <span className="font-notojp font-medium text-[10px]">{hubNews.hub_sub_name}</span>
                        )}
                        {hubNewsCount !== undefined && (
                            <span className="shrink-0 font-cantarell font-bold text-[10px] tabular-nums">
                                事例{hubNewsCount}件
                            </span>
                        )}
                    </button>
                ) : (
                    <div className="flex flex-wrap justify-start items-baseline w-full gap-x-1">
                        <div className="font-notojp font-medium text-xs text-color-co-gray-dark">
                            {hubNews.hub_name}
                        </div>
                        {hubNews.hub_sub_name && (
                            <div className="font-notojp font-medium text-[10px] text-color-co-gray-dark">
                                {hubNews.hub_sub_name}
                            </div>
                        )}
                    </div>
                )}
                <div className="font-corp font-bold text-base text-color-base pt-1 pb-1">{hubNews.title}</div>
                <div className="font-notojp font-medium text-[10px] text-color-base flex-grow">
                    {hubNews.description}
                </div>
                <div className="flex justify-end items-center w-full font-cantarell font-bold text-[10px] text-color-base pt-3">
                    <IconAdaptive
                        Icon={Clock3}
                        strokeWidth={2.0}
                        baseSize={14}
                        breakpoints={{ md: 14 }}
                        className="mr-[2px]"
                    />
                    <TruncateText
                        text={formatDateStringWithDelimiter(hubNews.published_date)}
                        maxLength={40}
                        breakpoints={{ md: 30 }}
                    />
                </div>
            </div>
        </div>
    );
}
